import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl, formatApiFetchError } from '../utils/apiConfig';
import {
  dedupeCoursesForDropdown,
  collapseCourseDisplayName,
  normalizeStudentCoursesArray,
} from '../utils/coursesCatalog';
import { BASE_COURSE_TYPES } from '../utils/courseTypes';

const COURSE_TYPES = BASE_COURSE_TYPES;

export default function CourseBuilder({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [grades, setGrades] = useState([]);
  const [existingCourses, setExistingCourses] = useState([]);
  
  // Form state
  const [courseName, setCourseName] = useState('');
  const [courseSummary, setCourseSummary] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [selectedCourseType, setSelectedCourseType] = useState(null);
  /** External only: [{ break_name, url }, ...] */
  const [externalBreakLinks, setExternalBreakLinks] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef(null);

  const setCourseType = (t) => {
    setSelectedCourseType(t);
    if (t === 'External') {
      setExternalBreakLinks((prev) =>
        prev.length === 0 ? [{ break_name: '', url: '' }] : prev
      );
    } else {
      setExternalBreakLinks([]);
    }
  };

  const updateExternalRow = (index, field, value) => {
    setExternalBreakLinks((rows) => {
      const next = [...rows];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeExternalRow = (index) => {
    setExternalBreakLinks((rows) => rows.filter((_, i) => i !== index));
  };

  const addExternalRow = () => {
    setExternalBreakLinks((rows) => [...rows, { break_name: '', url: '' }]);
  };

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetchUser(userId);
    fetchGrades();
    fetchExistingCourses();
  }, []);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        setGrades(data.sort((a, b) => a.grade_rank_order - b.grade_rank_order));
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchExistingCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setExistingCourses(dedupeCoursesForDropdown(list));
      }
    } catch (error) {
      console.error('Error fetching existing courses:', error);
    }
  };

  const handleReset = () => {
    setCourseName('');
    setCourseSummary('');
    setSelectedGradeId(null);
    setSelectedCourseType(null);
    setExternalBreakLinks([]);
    setSaveStatus(null);
    setErrorMessage('');
  };

  const showSaveFeedback = (status, message) => {
    setSaveStatus(status);
    setErrorMessage(message || '');
    console.log(`[CourseBuilder] save ${status}:`, message || '(no message)');
    requestAnimationFrame(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
  };

  const appendCourseToUserProfile = async (userId, courseName, currentCourses) => {
    const trimmed = collapseCourseDisplayName(courseName);
    if (!userId || !trimmed) return false;
    const existing = normalizeStudentCoursesArray(currentCourses);
    if (existing.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
      console.log('[CourseBuilder] course already on user profile:', trimmed);
      return true;
    }
    const next = [...existing, trimmed];
    try {
      const res = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(userId))}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courses: next }),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.warn('[CourseBuilder] Failed to add course to profile:', res.status, errText);
        return false;
      }
      const updated = await res.json();
      setCurrentUser(updated);
      console.log('[CourseBuilder] Added course to profile:', trimmed);
      return true;
    } catch (error) {
      console.warn('[CourseBuilder] Failed to add course to profile:', error);
      return false;
    }
  };

  const handleSave = async () => {
    const trimmedName = courseName.trim();
    console.log('[CourseBuilder] Save clicked', {
      courseName: trimmedName,
      courseType: selectedCourseType,
      gradeId: selectedGradeId,
    });

    if (!trimmedName) {
      showSaveFeedback('error', 'Course Name is required.');
      return;
    }

    const duplicate = existingCourses.find(
      (c) => String(c.course_name || '').trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate) {
      showSaveFeedback(
        'error',
        `A course named "${duplicate.course_name}" already exists (id ${duplicate.id}). Pick a different name or use Course Editor to update it.`
      );
      return;
    }

    if (saving) {
      console.log('[CourseBuilder] Save ignored — request already in flight');
      return;
    }

    setSaving(true);
    setSaveStatus(null);
    setErrorMessage('');

    try {
      const selectedGrade = selectedGradeId
        ? grades.find((g) => g.id === selectedGradeId)?.grade_name
        : null;

      const externalPayload =
        selectedCourseType === 'External'
          ? externalBreakLinks
              .map((r) => ({
                break_name: (r.break_name || '').trim(),
                url: (r.url || '').trim(),
              }))
              .filter((r) => r.break_name || r.url)
          : null;

      const payload = {
        course_name: trimmedName,
        associated_grade: selectedGrade,
        course_type: selectedCourseType || null,
        total_breaks: 0,
        summary: courseSummary.trim() ? courseSummary.trim() : null,
        external_break_links:
          selectedCourseType === 'External' && externalPayload?.length
            ? externalPayload
            : null,
      };

      console.log('[CourseBuilder] POST /api/courses', payload);

      const response = await fetch(`${getApiUrl()}/api/courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responseData = null;
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseData = { raw: responseText };
      }

      console.log('[CourseBuilder] POST /api/courses response', {
        status: response.status,
        ok: response.ok,
        body: responseData,
      });

      if (response.ok) {
        const created = responseData;
        const savedName = created?.course_name || trimmedName;
        let profileNote = '';
        if (currentUser?.id) {
          const onProfile = await appendCourseToUserProfile(
            currentUser.id,
            savedName,
            currentUser.courses
          );
          profileNote = onProfile
            ? ' It will appear under My Courses.'
            : ' Could not add it to your profile courses list — you may need to assign it manually.';
        }
        showSaveFeedback(
          'success',
          `Course "${savedName}" saved successfully (id ${created?.id ?? '?'}).${profileNote}`
        );
        void fetchExistingCourses();
      } else {
        const apiError =
          responseData?.error ||
          responseData?.message ||
          (typeof responseData?.raw === 'string' && responseData.raw.slice(0, 200)) ||
          `Server returned ${response.status}`;
        let hint = apiError;
        if (response.status === 409) {
          hint = `${apiError} Use a different course name.`;
        } else if (response.status === 400 && String(apiError).toLowerCase().includes('grade')) {
          hint = `${apiError} Try choosing a different Associated Grade or leave it as (None).`;
        } else if (response.status >= 500) {
          hint = `${apiError} Check the backend terminal for details.`;
        }
        showSaveFeedback('error', hint);
      }
    } catch (error) {
      const msg = formatApiFetchError(error, getApiUrl());
      console.error('[CourseBuilder] Save failed:', error);
      showSaveFeedback('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const renderSaveToast = () =>
    saveStatus ? (
      <View
        style={[
          styles.saveToast,
          saveStatus === 'success' ? styles.saveToastSuccess : styles.saveToastError,
        ]}
        nativeID="course-builder-save-toast"
        {...(Platform.OS === 'web'
          ? {
              dataSet: {
                courseBuilderSaveToast: 'true',
                courseBuilderSaveToastType: saveStatus,
              },
            }
          : {})}
      >
        <Text
          style={[
            styles.saveToastMessage,
            saveStatus === 'success' && styles.saveToastMessageSuccess,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { courseBuilderSaveToastText: 'true' } }
            : {})}
        >
          {saveStatus === 'success'
            ? errorMessage || 'Course saved successfully.'
            : errorMessage || 'Failed to save course. Please try again.'}
        </Text>
        <TouchableOpacity
          onPress={() => {
            setSaveStatus(null);
            setErrorMessage('');
          }}
          style={styles.saveToastClose}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          nativeID="course-builder-save-toast-close"
          {...(Platform.OS === 'web'
            ? { dataSet: { courseBuilderSaveToastClose: 'true' } }
            : {})}
        >
          <Text style={styles.saveToastCloseText}>✕</Text>
        </TouchableOpacity>
      </View>
    ) : null;

  const styles = getStyles(theme);

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'course-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Course Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {/* Title Section */}
        <View style={styles.titleSection}>
          <Text style={styles.title}>Course Builder</Text>
          <Text style={styles.subtitle}>
            A lot of courses have been created so far. These are specialised training for selected students. Create a Course here.
          </Text>
        </View>

        {/* Form Fields */}
        <View style={styles.form}>
          {/* Existing Courses Check */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Check if the course exists already before creating it here:</Text>
            <View style={styles.dropdownContainer}>
              {existingCourses.length > 0 ? (
                existingCourses.map((course) => (
                  <TouchableOpacity
                    key={course.id}
                    style={styles.dropdownOption}
                    onPress={() => {
                      // Just show the course info, don't select it
                      Alert.alert(
                        'Course Exists',
                        `Course "${course.course_name}" already exists.\n\nAssociated Grade: ${course.associated_grade || 'None'}\nCourse Type: ${course.course_type || 'None'}${course.summary ? `\n\nSummary: ${course.summary.slice(0, 200)}${course.summary.length > 200 ? '…' : ''}` : ''}`,
                        [{ text: 'OK' }]
                      );
                    }}
                  >
                    <Text style={styles.dropdownOptionText}>
                      {course.course_name}
                      {course.associated_grade && ` (${course.associated_grade})`}
                      {course.course_type && ` - ${course.course_type}`}
                    </Text>
                  </TouchableOpacity>
                ))
              ) : (
                <View style={styles.emptyDropdown}>
                  <Text style={styles.emptyDropdownText}>No existing courses found</Text>
                </View>
              )}
            </View>
          </View>

          {/* Course Name */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Course Name *</Text>
            <TextInput
              style={styles.input}
              value={courseName}
              onChangeText={setCourseName}
              placeholder="Enter course name"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            />
          </View>

          {/* Associated Grade */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Associated Grade</Text>
            <View style={styles.dropdownContainer}>
              <TouchableOpacity
                style={[
                  styles.dropdownOption,
                  selectedGradeId === null && styles.dropdownOptionSelected,
                ]}
                onPress={() => setSelectedGradeId(null)}
              >
                <Text style={[
                  styles.dropdownOptionText,
                  selectedGradeId === null && styles.dropdownOptionTextSelected,
                ]}>
                  (None)
                </Text>
              </TouchableOpacity>
              {grades.map((grade) => (
                <TouchableOpacity
                  key={grade.id}
                  style={[
                    styles.dropdownOption,
                    selectedGradeId === grade.id && styles.dropdownOptionSelected,
                  ]}
                  onPress={() => setSelectedGradeId(grade.id)}
                >
                  <Text style={[
                    styles.dropdownOptionText,
                    selectedGradeId === grade.id && styles.dropdownOptionTextSelected,
                  ]}>
                    {grade.grade_name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Summary */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Summary</Text>
            <Text style={styles.helpText}>
              Optional long description for this course (visible in the catalog when you surface it in the app).
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={courseSummary}
              onChangeText={setCourseSummary}
              placeholder="Course summary…"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              multiline
              numberOfLines={6}
            />
          </View>

          {/* Course Type */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Course Type</Text>
            <View style={styles.dropdownContainer}>
              <TouchableOpacity
                style={[
                  styles.dropdownOption,
                  selectedCourseType === null && styles.dropdownOptionSelected,
                ]}
                onPress={() => setCourseType(null)}
              >
                <Text style={[
                  styles.dropdownOptionText,
                  selectedCourseType === null && styles.dropdownOptionTextSelected,
                ]}>
                  (None)
                </Text>
              </TouchableOpacity>
              {COURSE_TYPES.map((courseType) => (
                <TouchableOpacity
                  key={courseType}
                  style={[
                    styles.dropdownOption,
                    selectedCourseType === courseType && styles.dropdownOptionSelected,
                  ]}
                  onPress={() => setCourseType(courseType)}
                >
                  <Text style={[
                    styles.dropdownOptionText,
                    selectedCourseType === courseType && styles.dropdownOptionTextSelected,
                  ]}>
                    {courseType}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* External-only: break name → URL pairs */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>External break links</Text>
            {selectedCourseType === 'External' ? (
              <>
                <Text style={styles.helpText}>
                  One row per break: break name (key) and URL (value). Stored as an array of
                  objects with break_name and url on the course.
                </Text>
                {externalBreakLinks.map((row, idx) => (
                  <View key={`ext-${idx}`} style={styles.externalLinkRow}>
                    <TextInput
                      style={[styles.input, styles.externalLinkInputName]}
                      value={row.break_name}
                      onChangeText={(v) => updateExternalRow(idx, 'break_name', v)}
                      placeholder="Break name"
                      placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    />
                    <TextInput
                      style={[styles.input, styles.externalLinkInputUrl]}
                      value={row.url}
                      onChangeText={(v) => updateExternalRow(idx, 'url', v)}
                      placeholder="https://…"
                      placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <TouchableOpacity
                      style={styles.externalLinkRemove}
                      onPress={() => removeExternalRow(idx)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Text style={styles.externalLinkRemoveText}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
                <TouchableOpacity style={styles.addLinkButton} onPress={addExternalRow}>
                  <Text style={styles.addLinkButtonText}>+ Add break link</Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={styles.helpText}>
                N/A — only courses with course type &quot;External&quot; can have break name / URL
                pairs. Other types store this field as blank (null).
              </Text>
            )}
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.resetButton]}
              onPress={handleReset}
              disabled={saving}
            >
              <Text style={styles.buttonText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton, saving && styles.buttonDisabled]}
              onPress={() => void handleSave()}
              disabled={saving}
            >
              <Text style={styles.buttonText}>{saving ? 'Saving…' : 'Save'}</Text>
            </TouchableOpacity>
          </View>

          {renderSaveToast()}
        </View>
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  titleSection: {
    marginBottom: 30,
    alignItems: 'center',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: theme.isDarkMode ? '#FDFD0A' : '#000000',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: 1,
    ...(Platform.OS === 'web' ? {
      fontFamily: 'serif, Georgia, "Times New Roman", Times, serif',
      textTransform: 'uppercase',
    } : {
      textTransform: 'uppercase',
    }),
  },
  subtitle: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.text,
    textAlign: 'center',
    paddingHorizontal: 20,
    lineHeight: 20,
    opacity: 0.8,
  },
  form: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  helpText: {
    fontSize: 12,
    color: theme.colors.text,
    opacity: 0.7,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  input: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  externalLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
    flexWrap: 'wrap',
  },
  externalLinkInputName: {
    flex: 1,
    minWidth: 120,
  },
  externalLinkInputUrl: {
    flex: 2,
    minWidth: 160,
  },
  externalLinkRemove: {
    padding: 8,
  },
  externalLinkRemoveText: {
    color: theme.colors.text,
    fontSize: 18,
    opacity: 0.7,
  },
  addLinkButton: {
    alignSelf: 'flex-start',
    marginTop: 4,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
  },
  addLinkButtonText: {
    color: theme.colors.heading,
    fontSize: 15,
    fontWeight: '600',
  },
  dropdownContainer: {
    marginTop: 8,
  },
  dropdownOption: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  dropdownOptionSelected: {
    backgroundColor: theme.colors.primary || '#d4af37',
    borderColor: theme.colors.primary || '#d4af37',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownOptionTextSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  emptyDropdown: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderStyle: 'dashed',
  },
  emptyDropdownText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.6,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
    gap: 15,
  },
  button: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButton: {
    backgroundColor: '#999',
  },
  saveButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  saveToast: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginTop: 16,
    width: '100%',
  },
  saveToastSuccess: {
    backgroundColor: '#d4af37',
  },
  saveToastError: {
    backgroundColor: '#c62828',
  },
  saveToastMessage: {
    flex: 1,
    color: '#ffffff',
    fontSize: 15,
    lineHeight: 20,
    marginRight: 8,
    paddingRight: 4,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  saveToastMessageSuccess: {
    color: '#000000',
    backgroundColor: 'transparent',
  },
  saveToastClose: {
    padding: 4,
    marginTop: -2,
    backgroundColor: 'transparent',
  },
  saveToastCloseText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
});
